import { RefObject, useEffect } from 'react';

// Attaches click-and-drag horizontal scroll with momentum/inertia to a
// scrollable element. Matches the feel of mobile touch scrolling rather
// than the immediate-stop behavior of raw scrollLeft manipulation.
//
// What this does:
//   - Mouse drag → pans the row in real time
//   - Suppresses the browser's native image / text drag (`dragstart`) so
//     grabbing an image inside the row doesn't trigger a download/ghost.
//   - On release, applies a decaying momentum scroll for ~400ms based on
//     the cursor velocity in the last 100ms before release.
//   - Click-vs-drag discrimination via DRAG_THRESHOLD so child tiles still
//     receive their onClick when the user actually clicks.
// `scrollRef` is the element whose `scrollLeft` we mutate; everything else is
// named in the options (review, 2026-09-16 — the positional list had reached
// three and a caller was passing a ref to nothing to mean "off").
export interface DragScrollOptions {
  /**
   * Where the pointer / click listeners attach. Defaults to `scrollRef`. Give
   * a separate target when the scroll element is wrapped by a positioning
   * container that also hosts overlay buttons (the Netflix-style arrow nav):
   * pointer events on those buttons bubble up to the wrapper, so attaching
   * there lets a drag start from anywhere in the row, arrows included.
   */
  eventTargetRef?: RefObject<HTMLElement>;
  /**
   * Re-attach when this changes. The refs are read once, when the effect
   * runs, so a caller whose scroller unmounts and comes back inside one mount
   * (the lobby's filter bar on a Sit & Go round trip; the tournament carousel
   * behind its empty state) passes something that changes across that trip.
   * Always-mounted callers leave it out.
   */
  reattachKey?: unknown;
  /**
   * `false` attaches nothing. For a caller that renders the same element with
   * and without a scroller of its own (TypeRow `inline`, where the parent bar
   * scrolls the row) — it says what it means, instead of handing over a ref
   * that points at nothing.
   */
  enabled?: boolean;
}

export const useDragScroll = (
  scrollRef: RefObject<HTMLElement>,
  { eventTargetRef, reattachKey, enabled = true }: DragScrollOptions = {}
): void => {
  useEffect(() => {
    if (!enabled) return;
    const el = scrollRef.current;
    if (!el) return;
    const eventTarget = eventTargetRef?.current ?? el;
    const DRAG_THRESHOLD = 5;
    const MOMENTUM_FRICTION = 0.86; // per-frame velocity decay (0..1)
    const MIN_VELOCITY = 0.08; // px/ms — below this, stop the momentum loop

    let isDown = false;
    let dragged = false;
    let startX = 0;
    let scrollLeft = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;
    let momentumRaf = 0;

    const cancelMomentum = () => {
      if (momentumRaf) {
        cancelAnimationFrame(momentumRaf);
        momentumRaf = 0;
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      if (e.button !== 0) return;
      cancelMomentum();
      isDown = true;
      dragged = false;
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
      lastX = e.pageX;
      lastT = performance.now();
      velocity = 0;
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isDown) return;
      // A row with nothing to scroll takes no drag. Without this a 6px twitch
      // on a one-tile row still set `dragged`, and the click that followed was
      // swallowed — a table that did not open. Off the phone every Sit & Go
      // rung is such a row (EN-10743), so it stops being a corner case there.
      if (el.scrollWidth <= el.clientWidth) return;
      const x = e.pageX - el.offsetLeft;
      const walk = x - startX;
      if (Math.abs(walk) > DRAG_THRESHOLD) {
        dragged = true;
        el.style.cursor = 'grabbing';
      }
      if (dragged) {
        e.preventDefault();
        el.scrollLeft = scrollLeft - walk;
        const now = performance.now();
        const dt = now - lastT;
        if (dt > 0) {
          // Velocity in px/ms (negative because scrollLeft decreases as
          // mouse moves right).
          velocity = ((e.pageX - lastX) / dt) * -1;
        }
        lastX = e.pageX;
        lastT = now;
      }
    };

    const runMomentum = () => {
      // Apply friction each frame; stop once velocity is below threshold or
      // the scroll hits a boundary.
      velocity *= MOMENTUM_FRICTION;
      if (Math.abs(velocity) < MIN_VELOCITY) {
        momentumRaf = 0;
        return;
      }
      // 16ms ≈ one 60fps frame
      const next = el.scrollLeft + velocity * 16;
      el.scrollLeft = next;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (next <= 0 || next >= maxScroll) {
        momentumRaf = 0;
        return;
      }
      momentumRaf = requestAnimationFrame(runMomentum);
    };

    const onPointerUp = () => {
      if (!isDown) return;
      isDown = false;
      el.style.cursor = '';
      // Only carry momentum if the user was actually dragging.
      if (dragged && Math.abs(velocity) > MIN_VELOCITY) {
        momentumRaf = requestAnimationFrame(runMomentum);
      }
    };

    const onPointerLeave = () => {
      // Don't kill momentum if the cursor leaves while not actively pressed.
      if (isDown) onPointerUp();
    };

    const onClickCapture = (e: MouseEvent) => {
      // Suppress click that follows a drag so child tiles don't open.
      if (dragged) {
        e.preventDefault();
        e.stopPropagation();
        dragged = false;
      }
    };

    const onDragStart = (e: DragEvent) => {
      // Kill the browser's native drag (the "ghost image" / download cursor
      // that fires when you mousedown on an <img>).
      e.preventDefault();
    };

    eventTarget.addEventListener('pointerdown', onPointerDown);
    eventTarget.addEventListener('pointermove', onPointerMove);
    eventTarget.addEventListener('pointerup', onPointerUp);
    eventTarget.addEventListener('pointerleave', onPointerLeave);
    eventTarget.addEventListener('pointercancel', onPointerUp);
    // Capture-phase click listener so clicks that follow a drag get killed
    // before any descendant (including overlay arrow buttons) sees them.
    eventTarget.addEventListener('click', onClickCapture, true);
    eventTarget.addEventListener('dragstart', onDragStart);

    return () => {
      cancelMomentum();
      eventTarget.removeEventListener('pointerdown', onPointerDown);
      eventTarget.removeEventListener('pointermove', onPointerMove);
      eventTarget.removeEventListener('pointerup', onPointerUp);
      eventTarget.removeEventListener('pointerleave', onPointerLeave);
      eventTarget.removeEventListener('pointercancel', onPointerUp);
      eventTarget.removeEventListener('click', onClickCapture, true);
      eventTarget.removeEventListener('dragstart', onDragStart);
    };
  }, [scrollRef, eventTargetRef, reattachKey, enabled]);
};
